import React, { useCallback, useState, useRef, useLayoutEffect, useMemo } from 'react';
import { useAnimatedScrollHandler, useSharedValue } from 'react-native-reanimated';
import { View, Alert, Keyboard } from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import { Spacer, AppView, TextButton, SettingsSection, NavigationHeader, SettingsItemInput, SCROLL_INDICATOR_INSETS, SCREEN_BOTTOM_PADDING, rem, } from 'design-system';
import Modal from 'utils/packages/Modal';
import StatusBar from 'design-system/StatusBar';
import { IconUnion } from 'design-system/icons';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import Button, { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import createStyleSheets from 'utils/createStyleSheets';
import { useAppSelector, useAppStore } from 'store/hooks';
import { useAutoFocusInput } from 'hooks/useAutoFocusInput';
import { muteErrorAndResultButThrows, useFetchOld } from 'hooks';
import InputFeedback from 'design-system/InputFeedback';
import { KeyboardAvoidingContainerNew } from 'design-system/KeyboardAvoidingContainerNew';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { useCommonInvestStyles } from './styles';
import { postRequiredNationalIdentifiers } from '../actions';
import { strings as onboardingTitleStrings } from '../components/OnboardingTitle';
import { selectMissingRequiredNationalityIdentifiers, selectShowMultiAccountTradingView } from '../reducers/selectors';
import { useAmplitudeOnMountEvent } from '../utils/analytics';
import useNavigateToNextScreen from '../hooks/useNavigateToNextScreen';
const strings = {
    alertTitle: 'Hold on 💫',
    alertInvalidInsuranceNumber: 'You need to enter a valid UK National Insurance Number.',
    countryOfTaxResidence: 'Country of tax residence',
    placeholder: 'Choose..',
};
const noop = () => { };
const ExtraInsuranceScreen = ({ route: { params }, navigation }) => {
    const colors = useColors();
    const store = useAppStore();
    const styles = useStyles(styleSet);
    const isInTab = params?.mountedOnAnotherScreen;
    const commonStyles = useCommonInvestStyles(isInTab);
    const scrollY = useSharedValue(0);
    const navigateToNextScreen = useNavigateToNextScreen();
    const identifier = params?.identifier;
    useLayoutEffect(() => {
        navigation.setOptions({
            headerTitle: '',
            headerTintColor: colors.text.primary,
        });
    }, [colors.text.primary]);
    useAmplitudeOnMountEvent(undefined, 'User Tax Screen', { id: identifier.type });
    const [nationalInsuranceNumber, setNationalInsuranceNumber] = useState('');
    const nationalInsuranceNumberRef = useRef(null);
    const showMultiAccountTradingView = useAppSelector(selectShowMultiAccountTradingView);
    const [, , loading, fetch] = useFetchOld(muteErrorAndResultButThrows);
    const handleNavigate = useCallback((didSkip) => {
        /**
         * If user does skip, we need to try to collect the next identifier if present
         */
        if (didSkip) {
            const requiredNationalIdentifiers = selectMissingRequiredNationalityIdentifiers(store.getState());
            if (requiredNationalIdentifiers?.length) {
                const nextIndex = requiredNationalIdentifiers.findIndex((curr) => curr.description === identifier.description && curr.type === identifier.type) + 1;
                const nextIdentifier = requiredNationalIdentifiers[nextIndex];
                if (nextIdentifier) {
                    navigation.replace('InvestExtraInsurance', {
                        identifier: nextIdentifier,
                    });
                    return;
                }
            }
        }
        /**
         * This is the case of portfolio block
         */
        if (showMultiAccountTradingView) {
            navigation.navigateDeprecated('Success', {
                title: 'Success',
                subTitle: 'Your details have been verified.',
                onContinue: () => {
                    navigation.popToTop();
                    return true;
                },
            });
            /**
             * This is the case of onboarding
             */
        }
        else {
            navigateToNextScreen();
        }
    }, [identifier.description, identifier.type, navigateToNextScreen, showMultiAccountTradingView]);
    const onSubmit = useCallback(async () => {
        if (identifier.regex) {
            let regExp;
            try {
                regExp = new RegExp(identifier.regex);
            }
            catch {
                Alert.alert(strings.alertTitle, 'Validation failed. Please contact support.');
                return;
            }
            const doesMatchRegex = nationalInsuranceNumber.match(regExp);
            if (!doesMatchRegex) {
                Alert.alert(strings.alertTitle, `Please enter a valid ${identifier.description}`);
                return;
            }
        }
        if (!nationalInsuranceNumber) {
            Alert.alert(strings.alertTitle, `Please enter a valid ${identifier.description}`);
            return;
        }
        nationalInsuranceNumberRef.current?.blur();
        try {
            await fetch(postRequiredNationalIdentifiers(identifier.type, identifier.country, nationalInsuranceNumber));
            handleNavigate(false);
        }
        catch {
            // useFetch never throws
        }
    }, [
        handleNavigate,
        identifier.country,
        identifier.description,
        identifier.regex,
        identifier.type,
        nationalInsuranceNumber,
    ]);
    const skip = useCallback(async () => {
        try {
            await fetch(postRequiredNationalIdentifiers(identifier.type, identifier.country, null));
            handleNavigate(true);
        }
        catch {
            //
        }
    }, [handleNavigate, identifier.country, identifier.type]);
    const onSkip = useCallback(() => {
        Keyboard.dismiss();
        Modal.showConfirmation({
            title: 'Confirmation',
            subtitle: 'I confirm that I don’t have this information.',
            confirmButtonTitle: 'Cancel',
            cancelButtonTitle: 'Confirm',
            onConfirm: noop,
            onPressCancel: skip,
        });
    }, [skip]);
    const onChangeNin = useCallback((text) => {
        setNationalInsuranceNumber(text.replace(' ', ''));
    }, []);
    const outputColors = useMemo(() => [colors.background.dark, colors.cards.onDark], [colors.background.dark, colors.cards.onDark]);
    const scrollHandler = useAnimatedScrollHandler((event) => {
        scrollY.value = event.contentOffset.y;
    });
    useAutoFocusInput(!nationalInsuranceNumber ? nationalInsuranceNumberRef : null);
    const { paddingHorizontalStyle } = useAppFrameDimensions(isInTab);
    return (<AppView withFrame={false}>
      <StatusBar barStyle="dark-content" animated/>
      <View style={styles.flex} pointerEvents={loading ? 'none' : 'auto'}>
        <NavigationHeader scrollY={scrollY} hideTitleOnScroll regularTitleHeight outputColors={outputColors} isInTab={isInTab} rightContent={<View style={!!isInTab && commonStyles.flexEnd}>
              <HelpScreenButton />
            </View>} title=""/>
        <KeyboardAwareScrollView mode="layout" keyboardShouldPersistTaps="handled" contentContainerStyle={paddingHorizontalStyle} scrollEventThrottle={16} onScroll={scrollHandler} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS} extraKeyboardSpace={SCREEN_BOTTOM_PADDING} bottomOffset={HEIGHT_PLUS_PADDING + rem(120)}>
          <ScreenInfoHeader title={`What is your ${identifier.description}?`} subTitle={onboardingTitleStrings.Insurance.subtitle}/>
          <SettingsSection noMarginTop>
            <SettingsItemInput testID="input" spellCheck={false} autoCorrect={false} icon={<IconUnion />} onChangeText={onChangeNin} onSubmitEditing={onSubmit} autoCapitalize="characters" value={nationalInsuranceNumber} ref={nationalInsuranceNumberRef} description={identifier.description} placeholder={identifier.placeholder || strings.placeholder}/>
          </SettingsSection>
          <Spacer h={16}/>
          {!!identifier.information && <InputFeedback type="info">{identifier.information}</InputFeedback>}
        </KeyboardAwareScrollView>
        <KeyboardAvoidingContainerNew sticky withFrame isInTab={isInTab}>
          {!!identifier.canBeNull && (<View style={styles.textBtnWrapper}>
              <TextButton text="I don’t have this information" onPress={onSkip}/>
            </View>)}
          <Button brand title="Continue" onPress={onSubmit} isFetching={loading}/>
        </KeyboardAvoidingContainerNew>
      </View>
    </AppView>);
};
const styleSet = createStyleSheets(() => ({
    flex: {
        flex: 1,
    },
    textBtnWrapper: {
        alignItems: 'center',
        marginBottom: rem(16),
    },
}));
export default ExtraInsuranceScreen;
